import React, { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { generateHTML } from '../../utils/exportHTML';
import { replaceShortcodesWithExamples } from '../../utils/adAttributes';
import { API } from '../../services/api';
import config from '../../config';
import './TemplateThumbnail.css';

// Utiliser l'URL de thumbnail pour les templates (même pattern que signatures)

function TemplateThumbnail({
    templateId,
    uid,
    templateData = null,
    updatedAt,
    containerWidth = 600,
    width = 300,
    height = 200,
    onClick
}) {
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (templateId && uid) {
            generateThumbnail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, uid, updatedAt]);

    const generateThumbnail = async () => {
        setLoading(true);
        setError(false);

        try {
            // 1. Essayer de charger depuis le backend
            try {
                const backendUrl = config.getThumbnailUrl(uid);
                const response = await fetch(backendUrl, {
                    cache: 'no-cache'
                });

                if (response.ok) {
                    // Le thumbnail existe sur le backend
                    const blob = await response.blob();
                    const dataUrl = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });

                    setThumbnailUrl(dataUrl);
                    setLoading(false);
                    return;
                }
            } catch (backendError) {
                console.log('Backend thumbnail not found, generating client-side...');
            }

            // 2. Fallback : Générer côté client
            // Récupérer les données du modèle si nécessaire
            let gridData = templateData?.grid_data;
            let tmplContainerWidth = templateData?.container_width || containerWidth;

            if (!gridData) {
                const response = await API.getTemplate(templateId);
                if (!response || !response.data) {
                    throw new Error('Modèle non trouvé');
                }
                gridData = response.data.template.grid_data;
                tmplContainerWidth = response.data.template.container_width || containerWidth;
            }

            // Générer le HTML du modèle et remplacer les shortcodes
            let html = generateHTML(gridData, tmplContainerWidth);
            html = replaceShortcodesWithExamples(html);

            // Créer un conteneur invisible pour le rendu
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            container.style.width = `${tmplContainerWidth}px`;

            // Styles de normalisation pour un rendu cohérent
            container.style.boxSizing = 'border-box';
            container.style.margin = '0';
            container.style.padding = '0';
            container.style.lineHeight = '1';
            container.style.fontSize = '16px';
            container.style.fontFamily = 'Arial, sans-serif';

            container.innerHTML = html;
            document.body.appendChild(container);

            // Attendre que les images soient chargées
            const images = container.querySelectorAll('img');
            console.log(`[TemplateThumbnail] Waiting for ${images.length} images to load...`);

            await Promise.all(Array.from(images).map((img, index) => {
                if (img.complete && img.naturalHeight !== 0) {
                    console.log(`[TemplateThumbnail] Image ${index} already loaded:`, img.src);
                    return Promise.resolve();
                }
                return new Promise((resolve) => {
                    img.onload = () => {
                        console.log(`[TemplateThumbnail] Image ${index} loaded:`, img.src);
                        resolve();
                    };
                    img.onerror = () => {
                        console.warn(`[TemplateThumbnail] Image ${index} failed to load:`, img.src);
                        resolve(); // Continue même si une image échoue
                    };
                    // Augmenter le timeout à 5 secondes
                    setTimeout(() => {
                        console.warn(`[TemplateThumbnail] Image ${index} timeout:`, img.src);
                        resolve();
                    }, 5000);
                });
            }));

            console.log('[TemplateThumbnail] All images processed, capturing with html2canvas...');

            // Capturer avec html2canvas
            const canvas = await html2canvas(container, {
                scale: 1,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: true, // Activer les logs pour déboguer
                imageTimeout: 5000, // Timeout pour les images
                removeContainer: false // Ne pas supprimer le container automatiquement
            });

            const thumbnail = canvas.toDataURL('image/png');

            // Nettoyer le DOM
            document.body.removeChild(container);

            // Uploader le thumbnail au backend pour les prochains chargements
            try {
                // Convertir dataURL en blob
                const response = await fetch(thumbnail);
                const blob = await response.blob();

                await API.uploadTemplateThumbnail(templateId, blob);
                console.log('[TemplateThumbnail] Thumbnail uploaded to backend successfully');
            } catch (uploadError) {
                console.warn('[TemplateThumbnail] Failed to upload thumbnail to backend:', uploadError);
                // Ne pas bloquer l'affichage si l'upload échoue
            }

            // Afficher le thumbnail
            setThumbnailUrl(thumbnail);
            setLoading(false);

        } catch (err) {
            console.error('Erreur lors de la génération du thumbnail:', err);
            setError(true);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div
                className="template-thumbnail loading"
                style={{ width: `${width}px`, height: `${height}px` }}
                onClick={onClick}
            >
                <div className="thumbnail-spinner"></div>
                <span>Génération...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="template-thumbnail error"
                style={{ width: `${width}px`, height: `${height}px` }}
                onClick={onClick}
            >
                <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Aperçu non disponible</span>
            </div>
        );
    }

    return (
        <div
            className="template-thumbnail"
            style={{ width: `${width}px`, height: `${height}px` }}
            onClick={onClick}
        >
            <img
                src={thumbnailUrl}
                alt="Aperçu du modèle"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
        </div>
    );
}

export default TemplateThumbnail;
